import { useState } from "react";
import {
  Button,
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
  DenkerLogo,
  Icons,
  ProjectAvatar,
  surfaceHoverClassName,
  surfaceRoleAttributes,
  surfaceRoleClassName,
} from "@/components/ui";
import { cn } from "@/lib/cn";
import {
  MOCK_PROJECTS,
  STATE_OPTIONS,
  type MockProject,
  type WorkState,
} from "@/workshop/data/denker-presence-switcher";
import {
  ActiveWorkDots,
  SwitcherPanel,
} from "@/workshop/components/canvas/denker-presence-switcher-panels";

function stateRingClassName(state: WorkState) {
  if (state === "working") {
    return "border-accent shadow-glow-accent motion-safe:animate-spin motion-reduce:animate-none";
  }
  if (state === "speaking") {
    return "border-sky-300 shadow-[0_0_22px_rgba(100,210,255,0.42)] motion-safe:animate-pulse motion-reduce:animate-none";
  }
  if (state === "listening") {
    return "border-accent shadow-glow-accent-sm motion-safe:animate-pulse motion-reduce:animate-none";
  }
  if (state === "waiting") return "border-amber-300 shadow-[0_0_18px_rgba(255,214,10,0.28)]";
  if (state === "error") return "border-red-400 shadow-[0_0_18px_rgba(248,113,113,0.34)]";
  return "border-glass-stroke-light";
}

function MiniFrame({ title, tone }: { title: string; tone: string }) {
  return (
    <div className="rounded-[18px] border border-glass-stroke-subtle bg-glass-fill p-4 shadow-glass-sm backdrop-blur-glass-sm">
      <div className={cn("mb-4 h-2 w-20 rounded-full", tone)} />
      <div className="grid grid-cols-[1fr_0.7fr] gap-2">
        <div className="h-20 rounded-xl bg-glass-fill-heavy" />
        <div className="h-20 rounded-xl bg-glass-fill" />
      </div>
      <p className="mt-3 truncate text-appkit-caption font-medium text-secondary">
        {title}
      </p>
    </div>
  );
}

function StateButton({
  option,
  active,
  onClick,
}: {
  option: (typeof STATE_OPTIONS)[number];
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "rounded-[18px] border px-3 py-2 text-left transition-colors",
        active
          ? "border-accent bg-accent-muted text-primary"
          : "border-glass-stroke-subtle bg-glass-fill text-secondary hover:bg-glass-fill-heavy",
      )}
      aria-pressed={active}
      data-testid={`denker-presence-state-${option.id}`}
    >
      <span className="block text-appkit-caption font-semibold">{option.label}</span>
      <span className="mt-0.5 block text-appkit-mini text-muted">{option.caption}</span>
    </button>
  );
}

function createMockProject(projects: MockProject[]): MockProject {
  const projectNumber =
    projects.filter((project) => project.id.startsWith("project-")).length + 1;
  return {
    id: `project-${projectNumber}`,
    name: `New Project ${projectNumber}`,
    color: "#FF375F",
    kind: "project",
    spaces: [
      {
        id: `project-${projectNumber}-space`,
        name: "Space 1",
        activeWork: 0,
      },
    ],
  };
}

export function DenkerPresenceSwitcher() {
  const [state, setState] = useState<WorkState>("working");
  const [projects, setProjects] = useState(MOCK_PROJECTS);
  const [selectedProjectId, setSelectedProjectId] = useState("home");
  const [selectedSpaceId, setSelectedSpaceId] = useState("home-today");
  const [hovered, setHovered] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [switcherOpen, setSwitcherOpen] = useState(false);
  const [notice, setNotice] = useState("No command selected yet.");

  const selectedProject =
    projects.find((project) => project.id === selectedProjectId) ?? projects[0]!;
  const selectedSpace =
    selectedProject.spaces.find((space) => space.id === selectedSpaceId) ??
    selectedProject.spaces[0]!;
  const activeWorkCount = state === "working" ? 3 : state === "waiting" ? 1 : 0;
  const expanded = hovered || pinned || switcherOpen;

  function selectProject(projectId: string) {
    const project = projects.find((item) => item.id === projectId);
    if (!project) return;
    setSelectedProjectId(project.id);
    setSelectedSpaceId(project.spaces[0]?.id ?? "");
  }

  function createProject() {
    const project = createMockProject(projects);
    const firstSpace = project.spaces[0]!;
    setProjects((current) => [...current, project]);
    setSelectedProjectId(project.id);
    setSelectedSpaceId(firstSpace.id);
  }

  function createSpace() {
    setProjects((current) =>
      current.map((project) =>
        project.id === selectedProjectId
          ? {
              ...project,
              spaces: [
                ...project.spaces,
                {
                  id: `${project.id}-space-${project.spaces.length + 1}`,
                  name: `Space ${project.spaces.length + 1}`,
                  activeWork: 0,
                },
              ],
            }
          : project,
      ),
    );
  }

  return (
    <div
      className="dark min-h-screen bg-canvas px-8 py-7 text-primary"
      data-testid="denker-presence-page"
    >
      <div className="mb-6 max-w-3xl">
        <p className="text-appkit-mini font-semibold uppercase tracking-[0.16em] text-accent">
          Canvas chrome exploration
        </p>
        <h1 className="mt-2 text-2xl font-semibold">Denker Presence Switcher</h1>
        <p className="mt-2 max-w-2xl text-appkit-body leading-6 text-secondary">
          A compact-mode proposal where the collapsed UI is only the Denker logo.
          Hover reveals context, click pins it, and the switcher remains one layer deeper.
        </p>
      </div>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_320px]">
        <section className="relative min-h-[690px] overflow-hidden rounded-[32px] border border-glass-stroke-light bg-canvas shadow-glass">
          <div className="absolute inset-0 opacity-80">
            <div className="absolute left-[8%] top-[26%] w-56 rotate-[-4deg]">
              <MiniFrame title="Home scratch notes" tone="bg-accent" />
            </div>
            <div className="absolute right-[14%] top-[18%] w-72 rotate-[3deg]">
              <MiniFrame title="Agent output summary" tone="bg-sky-300" />
            </div>
            <div className="absolute bottom-[18%] left-[32%] w-64 rotate-[1deg]">
              <MiniFrame title="Questions waiting" tone="bg-amber-300" />
            </div>
          </div>

          <div
            className="absolute left-1/2 top-7 z-20 -translate-x-1/2"
            onPointerEnter={() => setHovered(true)}
            onPointerLeave={() => {
              if (!pinned && !switcherOpen) setHovered(false);
            }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => {
              if (!pinned && !switcherOpen) setHovered(false);
            }}
          >
            <div
              className={cn(
                surfaceRoleClassName("rail"),
                "flex h-14 items-center overflow-visible border border-glass-stroke-light px-1.5 shadow-glass transition-[width,padding] duration-300",
                expanded
                  ? "w-[360px] justify-between rounded-pill"
                  : "w-14 justify-center rounded-full",
              )}
              data-testid="denker-presence-shell"
              {...surfaceRoleAttributes("rail", {
                nativeLevel: "root",
                nativeGroup: "presence-switcher",
              })}
            >
              <ContextMenu>
                <ContextMenuTrigger asChild>
                  <button
                    type="button"
                    onClick={() => setPinned((current) => !current)}
                    className={cn(
                      surfaceHoverClassName(),
                      "relative grid h-11 w-11 shrink-0 place-items-center rounded-full text-primary transition-transform hover:scale-105 motion-reduce:transition-none",
                    )}
                    aria-label="Denker presence"
                    aria-pressed={pinned}
                    aria-haspopup="menu"
                    data-testid="denker-presence-bubble"
                  >
                    <span
                      className={cn(
                        "absolute inset-0 rounded-full border-2",
                        stateRingClassName(state),
                      )}
                    />
                    {state === "listening" ? (
                      <span className="absolute inset-2 rounded-full bg-accent-muted motion-safe:animate-ping motion-reduce:animate-none" />
                    ) : null}
                    <DenkerLogo
                      color="currentColor"
                      height={22}
                      className="relative z-10 text-primary"
                    />
                    {selectedProject.kind === "project" ? (
                      <span
                        className="absolute -right-0.5 bottom-1 h-2.5 w-2.5 rounded-full ring-2 ring-black/40"
                        style={{ backgroundColor: selectedProject.color }}
                      />
                    ) : null}
                    {state === "waiting" ? (
                      <span className="absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full bg-amber-300 ring-2 ring-black/40" />
                    ) : null}
                    {state === "error" ? (
                      <span className="absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full bg-red-400 ring-2 ring-black/40" />
                    ) : null}
                  </button>
                </ContextMenuTrigger>
                <ContextMenuContent className="w-64">
                  <ContextMenuItem
                    icon={<Icons.Maximize />}
                    onClick={() => {
                      setPinned(true);
                      setNotice("Workspace would expand from compact mode.");
                    }}
                    data-testid="denker-presence-command-workspace"
                  >
                    Open workspace
                  </ContextMenuItem>
                  <ContextMenuItem
                    icon={<Icons.FolderTree />}
                    onClick={() => {
                      setSwitcherOpen(true);
                      setNotice("Opened the full Home, project, and space switcher.");
                    }}
                    data-testid="denker-presence-command-switcher"
                  >
                    Switch Home or project
                  </ContextMenuItem>
                  <ContextMenuItem
                    icon={<Icons.Settings />}
                    onClick={() =>
                      setNotice("Settings command selected from compact mode.")
                    }
                    data-testid="denker-presence-command-settings"
                  >
                    Denker settings
                  </ContextMenuItem>
                  <ContextMenuSeparator />
                  <ContextMenuItem
                    icon={<Icons.Minus />}
                    onClick={() => {
                      setPinned(false);
                      setHovered(false);
                      setSwitcherOpen(false);
                      setNotice("Collapsed back to the Denker logo.");
                    }}
                    data-testid="denker-presence-command-hide"
                  >
                    Hide to logo
                  </ContextMenuItem>
                  <ContextMenuItem
                    destructive
                    icon={<Icons.X />}
                    onClick={() =>
                      setNotice(
                        "Exit command selected; production would ask for confirmation.",
                      )
                    }
                    data-testid="denker-presence-command-exit"
                  >
                    Exit Denker
                  </ContextMenuItem>
                </ContextMenuContent>
              </ContextMenu>

              {expanded ? (
                <div className="flex min-w-0 flex-1 items-center justify-between gap-2 pl-2">
                  <button
                    type="button"
                    onClick={() => {
                      setSwitcherOpen((current) => !current);
                    }}
                    className="flex min-w-0 flex-1 items-center gap-2 rounded-pill px-2.5 py-1.5 text-left transition-colors hover:bg-glass-fill-heavy"
                    data-testid="denker-presence-context-open"
                  >
                    {selectedProject.kind === "home" ? (
                      <Icons.Sparkles className="h-4 w-4 shrink-0 text-accent" />
                    ) : (
                      <ProjectAvatar name={selectedProject.name} color={selectedProject.color} size="sm" />
                    )}
                    <span className="min-w-0 flex-1 truncate text-appkit-caption font-semibold text-primary">
                      {selectedProject.name}
                    </span>
                    <span className="max-w-24 truncate text-appkit-mini text-muted">
                      {selectedSpace.name}
                    </span>
                  </button>

                  <ActiveWorkDots count={activeWorkCount} />

                  <Button
                    type="button"
                    variant="ghost"
                    size="iconSm"
                    onClick={() => {
                      setPinned(false);
                      setHovered(false);
                      setSwitcherOpen(false);
                    }}
                    aria-label="Collapse to logo"
                    data-testid="denker-presence-collapse"
                  >
                    <Icons.Minus className="h-4 w-4" />
                  </Button>
                </div>
              ) : null}
            </div>

            {switcherOpen ? (
              <SwitcherPanel
                projects={projects}
                selectedProjectId={selectedProjectId}
                selectedSpaceId={selectedSpaceId}
                onSelectProject={selectProject}
                onSelectSpace={setSelectedSpaceId}
                onNewProject={createProject}
                onNewSpace={createSpace}
              />
            ) : null}
          </div>
        </section>

        <aside className="rounded-[28px] border border-glass-stroke-light bg-glass-fill p-5 shadow-glass backdrop-blur-glass-sm">
          <h2 className="text-appkit-subhead font-semibold text-primary">State presets</h2>
          <p className="mt-1 text-appkit-caption leading-5 text-secondary">
            These controls simulate what the live runtime would project onto the single bubble.
          </p>
          <div className="mt-4 grid grid-cols-2 gap-2">
            {STATE_OPTIONS.map((option) => (
              <StateButton
                key={option.id}
                option={option}
                active={state === option.id}
                onClick={() => setState(option.id)}
              />
            ))}
          </div>

          <div className="mt-6 border-t border-glass-stroke-subtle pt-5">
            <h3 className="text-appkit-caption font-semibold text-primary">Behavior contract</h3>
            <div className="mt-3 space-y-3 text-appkit-caption leading-5 text-secondary">
              <p>Collapsed state shows only Denker plus status motion and tiny context marks.</p>
              <p>Hover expands temporarily. Click pins the strip. Collapse returns to logo only.</p>
              <p>Right-click opens commands. The full project and space switcher is one click deeper.</p>
            </div>
            <div
              className="mt-4 rounded-[16px] border border-glass-stroke-subtle bg-glass-fill px-3 py-2 text-appkit-caption text-secondary"
              data-testid="denker-presence-command-notice"
            >
              {notice}
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
